import { FormViewerProps } from "interfaces/forms/FormViewerProps"
import { Form } from "models/forms/Form";
import { FormSubmission } from "models/projects/FormSubmission";
import type { UserSubmissionDto } from "models/projects/UserSubmissionDto";
import { SetStateAction, useEffect, useState } from "react";
import { ApiService } from "services/ApiService";
import FormElementList from "./FormElementList";
import { FormElement } from "models/forms/formElements/FormElement";
import { utilities } from "utils/utilities";
import { formSubmissionsApi } from "api/client/clients";
import { useParams } from "react-router-dom";

export const FormViewer: React.FC<FormViewerProps> = ({ formId, isDisabled, form: initialForm, onSubmit, workflowId, nodeId, projectId, instructionsJson }) => {

  const [form, setForm] = useState<Form | null>(initialForm);
  const [updatedElements, setUpdatedElements] = useState<FormElement[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { teamId } = useParams<{ teamId: string }>();

  const handleSubmission = async () => {
    // prepare a merged form using updatedElements if present
    if (!form) return;
    const mergedForm: Form = { ...form, elements: updatedElements && updatedElements.length > 0 ? updatedElements : form.elements } as Form;
    // Build a FormSubmission object (not posted yet) for future API usage
    const wfId = workflowId ?? '';
    const ndId = nodeId ?? '';
    const projId = projectId ?? utilities.DEFAULT_GUID;

    // Build the form submission instance using the FormSubmission model
    const formSubmission = new FormSubmission();
    formSubmission.userSubmissionId = utilities.DEFAULT_GUID;
    formSubmission.teamReview = null;
    // leave `status` as the model default (TeamReviewDecision.NotYetStarted)
    formSubmission.dateSubmitted = new Date();
    // attach the merged form
    formSubmission.formId = mergedForm.id;
    formSubmission.form = mergedForm;
    formSubmission.nodeId = ndId.toLocaleUpperCase();


    // Bubble up a UserSubmissionDto (base DTO) with context IDs
    if (typeof onSubmit === 'function') {
      try {
        const dto: UserSubmissionDto = {
          workflowId: wfId,
          nodeId: ndId,
          projectId: projId,
          userSubmission: formSubmission,
        };
        onSubmit(dto);
      } catch (err) {
        console.error('onSubmit handler threw', err);
      }
    }
    mergedForm.elements.forEach(c => console.log(c));
  };

  const handleUpdate = (elements: FormElement[]) => {
    setUpdatedElements(elements); // Keep track of updated form elements
  };

  useEffect(() => {
    if (!teamId) return;
    const loadForm = async () => {
      if (initialForm) {
        return;
      }
      setIsLoading(true);
      if (!formId || formId === "") {
        setIsLoading(false);
        return;
      }
      try {
        const data = (await ApiService.getForm(teamId, formId)).data;
        setForm(data);
        if (data?.elements)
          setUpdatedElements(data.elements);
      } catch (error) {
        console.error(error);
      }
      setIsLoading(false);
    };
    loadForm();
  }, [formId]);

  if (isLoading) {
    return <><h2>Loading . . .</h2></>
  }

  return (
    <div className="w-full h-full p-6 bg-white border border-gray-200 rounded-lg shadow-sm overflow-auto">
      <h2 className="text-2xl font-semibold mb-6">{form?.name}</h2>

      {instructionsJson && (
        <p className="mb-4 text-sm text-gray-600 whitespace-pre-wrap">
          {(() => { try { return JSON.parse(instructionsJson); } catch { return instructionsJson; } })()}
        </p>
      )}

      {form && (
        <>
          <FormElementList
            formElements={form.elements}
            onSubmit={handleUpdate}
            isDisabled={isDisabled ?? false}
          />
          
          { !isDisabled && (
          <button
            disabled={isDisabled ?? false}
            type="button"
            onClick={handleSubmission}
            className="mt-6 inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Submit
          </button>
          )}
        </>
      )}
    </div>
  );
}